// ChairmansBazooka — admin-only VIP-invite firing form.
//
// The Founder/Chairman meets a creator in person. This form lets them
// fire a personalised invite (email, SMS, or X DM) that grants the
// recipient:
//   1. Producer subscription with 1 quarter (90 days) comped on the
//      Chairman. Operates exactly like a paid Producer sub — the user
//      just skips the quarterly fee for this first quarter.
//   2. 1,000-credit Chairman's signing bonus + the quarter's normal
//      600-credit Producer allocation = 1,600 cr landing at signup.
//   3. A magic-link signup page that auto-fills first/last name and,
//      optionally, the username if the admin pre-picked one.
//
// Surfaced only inside the Total Site Master Admin section, scoped to
// `user.is_admin` (the parent page enforces that — we're just a form).
//
// Iter-58.6 (2026-06-01): split into 4 child components living under
// `./bazooka/` so this orchestrator stays under 250 lines and the
// previous complexity-55 footprint drops dramatically:
//   • BazookaChannelTabs   — email/SMS/X tab toggle + TAB_* constants
//   • BazookaFormFields    — channel-aware name/handle/phone/email rows
//   • BazookaResultPanel   — last-fire result card + copy actions
//   • BazookaHistoryPanel  — recent-fires table
//
// The form intentionally requires EXACTLY ONE of email/phone/x_handle
// — both is over-eager and ambiguous, neither is meaningless. The
// backend double-checks this rule.
import React, { useEffect, useState } from "react";
import { Send, Loader2, History, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import api from "../../lib/apiClient";
import ChairmansBazookaGroupPanel from "./ChairmansBazookaGroupPanel";
import BazookaChannelTabs, { TAB_EMAIL, TAB_SMS, TAB_X } from "./bazooka/BazookaChannelTabs";
import BazookaFormFields from "./bazooka/BazookaFormFields";
import BazookaResultPanel from "./bazooka/BazookaResultPanel";
import BazookaHistoryPanel from "./bazooka/BazookaHistoryPanel";

const ChairmansBazooka = () => {
  const [tab, setTab] = useState(TAB_EMAIL);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [usernamePick, setUsernamePick] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [xHandle, setXHandle] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const loadHistory = async () => {
    try {
      const r = await api.get("/admin/chairman/bazooka/history?limit=20");
      setHistory(r.data?.items || []);
    } catch (err) {
      // Non-fatal — keep form usable even if history endpoint hiccups.
      console.warn("bazooka history load failed", err);
    }
  };

  useEffect(() => {
    if (historyOpen) loadHistory();
  }, [historyOpen]);

  const reset = () => {
    setFirstName("");
    setLastName("");
    setUsernamePick("");
    setEmail("");
    setPhone("");
    setXHandle("");
    setLastResult(null);
  };

  const fire = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    // X-DM mode is the only channel where first/last are optional —
    // you might only know someone by their X handle. The backend
    // falls back to "@<handle>" salutation in that case.
    if (tab !== TAB_X) {
      if (!firstName.trim() || !lastName.trim()) {
        toast.error("First and last name are required for email/SMS.");
        return;
      }
    }
    const payload = {};
    if (firstName.trim()) payload.first_name = firstName.trim();
    if (lastName.trim()) payload.last_name = lastName.trim();
    if (usernamePick.trim()) payload.username = usernamePick.trim().toLowerCase();

    if (tab === TAB_EMAIL) {
      if (!email.trim()) { toast.error("Email is required."); return; }
      payload.email = email.trim();
    } else if (tab === TAB_SMS) {
      if (!phone.trim()) { toast.error("Phone is required."); return; }
      payload.phone = phone.trim();
    } else {
      // TAB_X — only the handle is required; everything else optional.
      const clean = xHandle.trim().replace(/^@/, "");
      if (!clean) { toast.error("X handle is required."); return; }
      payload.x_handle = clean;
    }

    setBusy(true);
    try {
      const r = await api.post("/admin/chairman/bazooka", payload);
      setLastResult(r.data);
      if (tab === TAB_X) {
        toast.success(
          `🎯 Invite minted for @${payload.x_handle}. Copy the DM template below + send it from x.com.`,
        );
      } else if (r.data?.delivered) {
        toast.success(`🎯 Bazooka fired at ${firstName} — delivery confirmed.`);
      } else {
        toast.warning(
          "Bazooka recorded but delivery wasn't confirmed by the provider. Use the invite URL below to share manually.",
        );
      }
      if (historyOpen) loadHistory();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Bazooka failed to fire.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      data-testid="chairmans-bazooka-card"
      className="bg-gradient-to-br from-amber-950/40 via-rose-950/40 to-gray-900 border-amber-500/40 mb-6"
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-amber-300 flex items-center gap-2">
          🎯 The Chairman's Bazooka
        </CardTitle>
        <CardDescription className="text-gray-400">
          Fire a personal Founder-invite at a creator you've met. Recipient
          gets <strong className="text-amber-200">Producer comped for 1 quarter (90 days)</strong> —
          operates exactly like a paid Producer sub, except you cover the quarterly fee — plus
          a <strong className="text-amber-200"> 1,000-credit Chairman's signing bonus</strong>.
          That's a total of <strong className="text-amber-200">1,600 cr</strong> landing the moment they sign up.
          Future top-ups and the next quarter's renewal require a real card on their end.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <BazookaChannelTabs tab={tab} onChange={setTab} />

        <form onSubmit={fire} className="space-y-3">
          <BazookaFormFields
            tab={tab}
            firstName={firstName} setFirstName={setFirstName}
            lastName={lastName} setLastName={setLastName}
            usernamePick={usernamePick} setUsernamePick={setUsernamePick}
            email={email} setEmail={setEmail}
            phone={phone} setPhone={setPhone}
            xHandle={xHandle} setXHandle={setXHandle}
          />

          <div className="flex gap-2 flex-wrap">
            <Button
              type="submit"
              disabled={busy}
              data-testid="bazooka-fire-btn"
              className="bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-400 hover:to-rose-400 text-gray-900 font-bold"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-1.5" />
              )}
              FIRE
            </Button>
            {lastResult && (
              <Button
                type="button"
                variant="ghost"
                onClick={reset}
                data-testid="bazooka-reset-btn"
                className="text-gray-400 hover:text-gray-200"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" /> New
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              onClick={() => setHistoryOpen((v) => !v)}
              data-testid="bazooka-history-toggle"
              className="text-gray-400 hover:text-gray-200 ml-auto"
            >
              <History className="h-3.5 w-3.5 mr-1" />
              {historyOpen ? "Hide" : "Show"} history
            </Button>
          </div>
        </form>

        <BazookaResultPanel lastResult={lastResult} />
        {historyOpen && <BazookaHistoryPanel history={history} />}

        {/* Group / chat-room invite link — separate panel because it
            doesn't need first/last/email/etc. Same backend benefits per
            claim (90d Producer + 1,600 cr), but one URL serves N
            people in a private chat room. */}
        <ChairmansBazookaGroupPanel />
      </CardContent>
    </Card>
  );
};

export default ChairmansBazooka;
